import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

const NEW = (kind) => ({ id: `new-${Date.now()}`, _new: true, kind, name: "New tier", desc: "", fee: 0, early_bird_pct: 0, capacity: 0, active: true, gst_inclusive: true });
const FIELDS = ["kind", "name", "desc", "fee", "early_bird_pct", "capacity", "active", "sort_order", "gst_inclusive"];

export function Fees({ eventId }) {
  const [rows, setRows] = useState(null);
  const [orig, setOrig] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/events/${eventId}/fee-tiers`).then((r) => {
    setRows(r.data); setOrig(Object.fromEntries(r.data.map((x) => [x.id, x])));
  }).catch((e) => toast.error(errText(e)));
  useEffect(() => { setRows(null); load(); }, [eventId]); // eslint-disable-line

  const upd = (id, k, v) => setRows(rows.map((r) => r.id === id ? { ...r, [k]: v } : r));
  const del = async (r) => {
    if (r._new) return setRows(rows.filter((x) => x.id !== r.id));
    try { await api.delete(`/fee-tiers/${r.id}`); toast.success(`${r.name} removed`); load(); } catch (e) { toast.error(errText(e)); }
  };

  const save = async () => {
    setBusy(true);
    const ops = rows.map((r, i) => {
      const body = { kind: r.kind, name: r.name, desc: r.desc || "", fee: Number(r.fee), early_bird_pct: Number(r.early_bird_pct), capacity: Number(r.capacity), active: !!r.active, sort_order: i, gst_inclusive: !!r.gst_inclusive };
      if (r._new) return api.post(`/events/${eventId}/fee-tiers`, body);
      const o = orig[r.id];
      const diff = Object.fromEntries(FIELDS.map((k) => [k, body[k]]).filter(([k, v]) => v !== o?.[k]));
      return Object.keys(diff).length ? api.patch(`/fee-tiers/${r.id}`, diff) : null;
    }).filter(Boolean);
    if (!ops.length) { setBusy(false); return toast.info("No changes"); }
    const res = await Promise.allSettled(ops);
    const failed = res.filter((x) => x.status === "rejected");
    failed.length ? toast.error(errText(failed[0].reason)) : toast.success(`Fees saved · ${ops.length} change${ops.length > 1 ? "s" : ""} logged`);
    setBusy(false); load();
  };

  if (!rows) return <div className="mono-label" data-testid="fees-loading">Loading fee tiers…</div>;
  return (
    <div className="space-y-6">
      {["member", "stall"].map((kind) => (
        <Panel className="p-6" key={kind}>
          <div className="flex items-center justify-between">
            <MonoLabel>{kind === "member" ? "Registration Tier Fees" : "Stall Tier Fees"}</MonoLabel>
            <button data-testid={`fees-add-${kind}`} onClick={() => setRows([...rows, NEW(kind)])} className="btn-ghost"><Plus size={14} /> Add tier</button>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left mono-label">
                  <th className="py-3 pr-3">Tier</th><th className="py-3 pr-3">Base Fee (₹)</th><th className="py-3 pr-3">Early Bird %</th>
                  <th className="py-3 pr-3">Capacity</th><th className="py-3 pr-3">Incl. GST</th><th className="py-3 pr-3">Active</th><th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.filter((r) => r.kind === kind).map((r, i) => (
                  <tr key={r.id} className="hairline-t" data-testid={`fee-row-${kind}-${i}`}>
                    <td className="py-3 pr-3 min-w-[220px]">
                      <input data-testid={`fee-name-${kind}-${i}`} className="input-x !py-2" value={r.name} onChange={(e) => upd(r.id, "name", e.target.value)} />
                      <input data-testid={`fee-desc-${kind}-${i}`} className="input-x !py-1 mt-1 !text-xs" placeholder="description" value={r.desc || ""} onChange={(e) => upd(r.id, "desc", e.target.value)} />
                    </td>
                    <td className="py-3 pr-3"><input data-testid={`fee-base-${kind}-${i}`} className="input-x !py-2 !w-32" value={r.fee} onChange={(e) => upd(r.id, "fee", e.target.value.replace(/\D/g, ""))} /></td>
                    <td className="py-3 pr-3"><input data-testid={`fee-eb-${kind}-${i}`} className="input-x !py-2 !w-24" value={r.early_bird_pct} onChange={(e) => upd(r.id, "early_bird_pct", e.target.value.replace(/\D/g, ""))} /></td>
                    <td className="py-3 pr-3"><input data-testid={`fee-cap-${kind}-${i}`} className="input-x !py-2 !w-24" value={r.capacity} onChange={(e) => upd(r.id, "capacity", e.target.value.replace(/\D/g, ""))} /></td>
                    <td className="py-3 pr-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none" title="If checked, the Base Fee already includes GST (public page shows exactly that amount). If unchecked, GST is added on top at checkout.">
                        <input data-testid={`fee-incl-gst-${kind}-${i}`} type="checkbox" checked={!!r.gst_inclusive} onChange={(e) => upd(r.id, "gst_inclusive", e.target.checked)} className="w-4 h-4 accent-[var(--red)]" />
                        <span className="mono-label" style={{ color: r.gst_inclusive ? "var(--text)" : "var(--taupe)" }}>{r.gst_inclusive ? "incl." : "+ GST"}</span>
                      </label>
                    </td>
                    <td className="py-3 pr-3"><Switch data-testid={`fee-active-${kind}-${i}`} checked={!!r.active} onCheckedChange={(v) => upd(r.id, "active", v)} /></td>
                    <td className="py-3 text-right"><button data-testid={`fee-del-${kind}-${i}`} onClick={() => del(r)} className="p-2 hover:text-[var(--red)]"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
      <button data-testid="fees-save-btn" className="btn-red" disabled={busy} onClick={save}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save fees
      </button>
    </div>
  );
}
